import createClient, { type Client, type Middleware } from 'openapi-fetch';
import type { components, paths } from '../generated/api';
import { appBase, csrfToken } from './page';

/**
 * Typed API access. `paths`/`components` are generated from the server's OpenAPI document
 * (`npm run codegen`, run automatically by `dotnet build`), so request bodies, routes and
 * responses are checked against the C# endpoints at compile time.
 *
 *   const result = await send(() => api().POST('/api/account/login', { body }));
 *   if (result.ok) result.data.redirectUrl; else result.problem.title;
 */

export type Schemas = components['schemas'];

/** RFC 9457 problem details as returned by the minimal API. */
export interface ProblemDetails {
  title?: string;
  status?: number;
  detail?: string;
  /** Machine-readable reason, e.g. "invalid_credentials", "locked_out", "csrf". */
  code?: string;
  /** Validation errors keyed by camelCase field name. */
  errors?: Record<string, string[]>;
  traceId?: string;
}

export type ApiResult<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; problem: ProblemDetails };

const safeMethods = new Set(['GET', 'HEAD', 'OPTIONS']);

/** Adds the antiforgery token to state-changing requests. */
const antiforgery: Middleware = {
  onRequest({ request }) {
    const token = csrfToken();
    if (token && !safeMethods.has(request.method)) {
      request.headers.set('X-CSRF-TOKEN', token);
    }
    return request;
  },
};

/** A client rooted at the app base (works under an IIS virtual directory). */
export function api(): Client<paths> {
  const client = createClient<paths>({
    baseUrl: appBase().replace(/\/$/, ''),
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    // Resolve fetch per call so tests can stub it.
    fetch: (request) => globalThis.fetch(request),
  });
  client.use(antiforgery);
  return client;
}

const networkProblem: ProblemDetails = {
  title: "Can't reach the server. Check your connection and try again.",
  code: 'network',
};

/** Runs an API call and normalizes it to data or problem details. Never throws for HTTP or network errors. */
export async function send<T>(call: () => Promise<{ data?: T; error?: unknown; response: Response }>): Promise<ApiResult<T>> {
  let result: Awaited<ReturnType<typeof call>>;
  try {
    result = await call();
  } catch {
    return { ok: false, status: 0, problem: networkProblem };
  }

  const status = result.response.status;
  if (result.response.ok) {
    return { ok: true, status, data: result.data as T };
  }

  const problem: ProblemDetails = isObject(result.error) ? (result.error as ProblemDetails) : {};
  return { ok: false, status, problem: { ...problem, title: problem.title ?? defaultTitle(status) } };
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function defaultTitle(status: number): string {
  if (status === 401) return 'Your session ended. Sign in again.';
  if (status === 403) return "You don't have permission to do that.";
  if (status === 429) return 'Too many attempts. Wait a minute and try again.';
  return 'Something went wrong. Try again.';
}

/** First message for each field, for binding to form inputs. */
export function fieldErrors(problem: ProblemDetails): Record<string, string> {
  return Object.fromEntries(Object.entries(problem.errors ?? {}).map(([field, messages]) => [field, messages[0] ?? '']));
}
